import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, untracked } from '@angular/core';
import { MatIcon } from '@angular/material/icon';

import { BaasI18n } from '@lvflanders/bar';

import { BarI18nPipe } from '../../i18n/bar-i18n.pipe';
import { BaasRemarkCounterOptions } from '../baas-remark-counter-options.model';
import { BaasRemarkOptions } from '../baas-remark-options.type';
import { _BAAS_REMARK_MAPPINGS } from '../remark-constants';

@Component({
    selector: 'baas-remark-summary',
    templateUrl: './baas-remark-summary.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatIcon, BarI18nPipe]
})
/** Represents a single remark */
export class BaasRemarkSummary {
    readonly #i18n = inject(BaasI18n);

    readonly _counters = model<BaasRemarkCounterOptions>(undefined, { alias: 'counters' });
    readonly remarks = input<BaasRemarkOptions[]>();

    protected readonly counters = computed(() => {
        if (!this._counters()) return [];
        return Object.entries(this._counters())
            .filter(([, counter]) => counter)
            .map(([type, counter]) => {
                return {
                    type: type,
                    color: _BAAS_REMARK_MAPPINGS[type].color,
                    icon: _BAAS_REMARK_MAPPINGS[type].class,
                    content: this.#defineTranslation(type, counter)
                };
            });
    });

    protected readonly totaal = computed(() => {
        if (!this._counters()) return 0;
        return this._counters().blocking + this._counters().important + this._counters().info;
    });

    constructor() {
        effect(() => {
            const remarks = this.remarks();
            untracked(() => {
                let counters = { blocking: 0, important: 0, info: 0 } as BaasRemarkCounterOptions;

                if (remarks && remarks.length) {
                    counters = remarks.reduce((counters: BaasRemarkCounterOptions, remark: BaasRemarkOptions) => {
                        switch (remark.type) {
                            case 'blocking':
                                counters.blocking++;
                                break;
                            case 'important':
                                counters.important++;
                                break;
                            case 'info':
                                counters.info++;
                                break;
                        }
                        return counters;
                    }, counters);
                }

                this._counters.set(counters);
            });
        });
    }

    #defineTranslation(type, counter): string {
        const translationKey = `DOMAIN_BAR_CONTROLS.REMARKS.REMARK_${type.toUpperCase()}_${counter > 1 ? 'N' : '1'}`;
        return this.#i18n.get(translationKey, {
            count: counter
        });
    }
}
